import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { ChevronLeft, ChevronRight, Pause, Play, Trophy, BookOpen, Activity as ActivityIcon, PieChart as PieIcon,
  Target, CalendarDays, Sun, Users, ShieldCheck, TrendingUp, Award, XCircle, UserCheck, Fingerprint, Crosshair,
  Sparkles, Star, Clock, MapPin, Moon } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_SLIDE_MS = 12000;
const REFRESH_MS = 12 * 60 * 60 * 1000;

// ---------- Design primitives ----------
const Frame = ({ title, subtitle, index, total, children, footerAccent, qrUrl, orgLogo, orgName }) => (
  <div className="relative w-full h-full bg-[#0b1220] text-white flex flex-col overflow-hidden">
    <div className="h-3 w-full bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500" />
    <div className="px-14 pt-7 pb-4 flex items-start justify-between gap-6">
      <div>
        <div className="text-slate-300 font-semibold tracking-wide" style={{ fontSize: 'clamp(16px, 1.2vw, 22px)' }}>{subtitle}</div>
        <h1 className="text-white font-black tracking-tight mt-1 leading-none" style={{ fontSize: 'clamp(40px, 4.2vw, 74px)' }}>{title}</h1>
      </div>
      {orgLogo ? (
        <img src={orgLogo} alt={orgName || 'Klubb'} className="h-9 md:h-11 opacity-80 object-contain" />
      ) : orgName ? (
        <div className="text-slate-400 font-bold whitespace-nowrap" style={{ fontSize: 'clamp(14px, 1vw, 20px)' }}>{orgName}</div>
      ) : null}
    </div>
    <div className="flex-1 min-h-0 px-14 pb-3 flex flex-col">{children}</div>
    {footerAccent && (
      <div className="mx-14 mb-3 rounded-lg bg-amber-500 text-slate-900 py-4 px-6 flex items-center justify-between font-bold" style={{ fontSize: 'clamp(14px, 1.1vw, 20px)' }}>
        {footerAccent}
      </div>
    )}
    <div className="mx-14 pb-3 border-t border-slate-800 pt-2 flex items-center justify-between text-slate-500 tracking-[0.25em] font-bold uppercase" style={{ fontSize: 'clamp(11px, 0.85vw, 15px)' }}>
      <span>AKTIVITETSSTATISTIKK</span>
      {!footerAccent && <span className="tracking-widest">Infoskjerm-visning <span className="text-amber-400/80 underline decoration-dotted">Aktivlogg</span></span>}
      <span>{index}</span>
    </div>
    {qrUrl && (
      <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-white rounded-lg p-2 shadow-xl" data-testid="signage-qr-code">
        <QRCodeSVG value={qrUrl} size={72} bgColor="#ffffff" fgColor="#0f172a" level="M" />
        <div className="text-slate-800 pr-2">
          <div className="font-black leading-tight" style={{ fontSize: '13px' }}>Logg trening</div>
          <div className="text-slate-500" style={{ fontSize: '10px' }}>Skann for å åpne</div>
        </div>
      </div>
    )}
  </div>
);

const scaleValueFont = (val, base = 9) => {
  const s = String(val ?? '');
  const len = s.length;
  if (len <= 2) return `clamp(72px, ${base}vw, 160px)`;
  if (len === 3) return `clamp(64px, ${base - 1}vw, 132px)`;
  if (len === 4) return `clamp(52px, ${base - 2}vw, 108px)`;
  if (len === 5) return `clamp(44px, ${base - 3}vw, 88px)`;
  return `clamp(32px, ${base - 4}vw, 70px)`;
};

const DeltaBadge = ({ pct, positive }) => {
  if (pct === null || pct === undefined || pct === 0) return null;
  const pos = positive !== undefined ? positive : pct >= 0;
  return (
    <div className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-black ${pos ? 'bg-emerald-500/25 text-emerald-100' : 'bg-rose-500/25 text-rose-100'}`}
         style={{ fontSize: 'clamp(12px, 1vw, 18px)' }}>
      <span>{pos ? '▲' : '▼'}</span><span>{Math.abs(pct)}%</span>
    </div>
  );
};

const BigKPI = ({ label, value, colorClass, deltaText, deltaPositive, deltaPct, Icon, valueScale = 9 }) => (
  <div className={`rounded-3xl px-12 py-12 flex flex-col shadow-2xl relative overflow-hidden ${colorClass}`} style={{ minHeight: '100%' }}>
    <div className="absolute top-10 left-10 w-14 h-14 rounded-xl bg-white/25 flex items-center justify-center">
      {Icon ? <Icon className="w-8 h-8 text-white" strokeWidth={2.5} /> : <div className="w-5 h-5 rounded-full bg-white" />}
    </div>
    {(deltaPct !== undefined && deltaPct !== null) && (
      <div className="absolute top-10 right-10"><DeltaBadge pct={deltaPct} positive={deltaPositive} /></div>
    )}
    <div className="flex-1 flex flex-col justify-center gap-3 mt-6">
      <div className="text-white/90 font-bold leading-tight tracking-wide" style={{ fontSize: 'clamp(17px, 1.35vw, 26px)' }}>{label}</div>
      <div className="text-white font-black leading-none tracking-tight" style={{ fontSize: scaleValueFont(value, valueScale) }}>{value}</div>
      {deltaText && (
        <div className={`font-bold mt-1 ${deltaPositive ? 'text-white' : 'text-white/85'}`} style={{ fontSize: 'clamp(13px, 1.05vw, 20px)' }}>
          {deltaPositive ? '↗' : '↘'} {deltaText}
        </div>
      )}
    </div>
  </div>
);

const RankRow = ({ rank, name, value, unit, dotColor = 'bg-amber-400' }) => (
  <div className="flex items-center gap-8 bg-slate-800/50 rounded-2xl px-8 py-6 border border-slate-700/60">
    <div className={`w-14 h-14 rounded-full flex items-center justify-center font-black text-slate-900 text-2xl shrink-0 ${dotColor}`}>{rank}</div>
    <div className="flex-1 min-w-0 font-bold truncate" style={{ fontSize: 'clamp(22px, 1.9vw, 38px)' }}>{name}</div>
    <div className="font-black text-amber-300 whitespace-nowrap" style={{ fontSize: 'clamp(24px, 2.1vw, 44px)' }}>
      {value} <span className="text-slate-400 font-bold" style={{ fontSize: 'clamp(12px, 0.9vw, 18px)' }}>{unit}</span>
    </div>
  </div>
);

const SectionTitle = ({ children, icon, dotColor = 'bg-amber-400' }) => (
  <div className="flex items-center gap-3 mb-4">
    {icon || <div className={`w-5 h-5 rounded-full ${dotColor}`} />}
    <h3 className="font-black tracking-tight text-white" style={{ fontSize: 'clamp(20px, 1.7vw, 32px)' }}>{children}</h3>
  </div>
);

const RECORD_ACCENTS = {
  amber: 'border-amber-500/30 text-amber-300',
  sky: 'border-sky-500/30 text-sky-300',
  emerald: 'border-emerald-500/30 text-emerald-300',
};

const fmtNoDate = (d) => {
  const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : d;
};

const RecordBox = ({ label, value, text, accent, testId }) => (
  <div className={`bg-slate-900/60 rounded-2xl px-6 py-4 border ${RECORD_ACCENTS[accent].split(' ')[0]}`} data-testid={testId}>
    <div className="text-slate-400 uppercase font-bold tracking-widest" style={{ fontSize: 'clamp(11px, 0.85vw, 15px)' }}>{label}</div>
    <div className={`font-black mt-1 leading-none ${RECORD_ACCENTS[accent].split(' ')[1]}`} style={{ fontSize: 'clamp(30px, 2.8vw, 60px)' }}>{value}</div>
    <div className="text-white/80 font-bold mt-1" style={{ fontSize: 'clamp(14px, 1.05vw, 21px)' }}>{text}</div>
  </div>
);

// Fade wrapper — mounts new slide on top with opacity crossfade
const FadeSlide = ({ k, children }) => {
  const [shown, setShown] = useState(k);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    if (k === shown) return;
    setFading(true);
    const t = setTimeout(() => { setShown(k); setFading(false); }, 320);
    return () => clearTimeout(t);
  }, [k, shown]);
  return (
    <div className="absolute inset-0 transition-opacity duration-300 ease-in-out"
         style={{ opacity: fading ? 0 : 1 }} data-testid="signage-slide">
      {children}
    </div>
  );
};

// Night mode overlay: 23:00 – 06:00
const useIsNight = (enabled) => {
  const [night, setNight] = useState(false);
  useEffect(() => {
    if (!enabled) { setNight(false); return; }
    const check = () => {
      const h = new Date().getHours();
      setNight(h >= 23 || h < 6);
    };
    check();
    const iv = setInterval(check, 60 * 1000);
    return () => clearInterval(iv);
  }, [enabled]);
  return night;
};

// ---------- Main component ----------
export default function SignageView() {
  const { orgId, token } = useParams();
  const [data, setData] = useState(null);
  const [merkeStats, setMerkeStats] = useState(null);
  const [err, setErr] = useState(null);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const configVersionRef = useRef(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/api/public/signage/${orgId}/${token}`);
      const newVer = r.data.config_version || 0;
      if (configVersionRef.current !== null && newVer && newVer !== configVersionRef.current) {
        // Innstillinger endret av admin – last presentasjonen på nytt
        window.location.reload();
        return;
      }
      configVersionRef.current = newVer;
      setData(r.data);
      // Hent merkeskyting-stats – ignorér feil (kanskje ikke aktivert)
      try {
        const rm = await axios.get(`${API_BASE}/api/public/merkeskyting/stats/${orgId}/${token}`);
        setMerkeStats(rm.data);
      } catch { setMerkeStats(null); }
    } catch (e) {
      setErr('Kunne ikke laste presentasjonsvisning: ' + (e?.response?.status || e.message));
    }
  }, [orgId, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(load, 15000); // 15s poll so admin-endringer trer i kraft raskt
    return () => clearInterval(iv);
  }, [load]);

  const settings = data?.settings || {};
  const isNight = useIsNight(settings.night_mode_enabled);
  const slideMs = (settings.slide_duration_seconds || 12) * 1000;
  const qrUrl = (settings.qr_registration_url || '').trim() || null;

  // Build ordered slide list based on toggles
  const slides = data ? buildSlides(data, qrUrl, merkeStats) : [];

  useEffect(() => {
    if (paused || !slides.length) return;
    timerRef.current = setInterval(() => setI(p => (p + 1) % slides.length), slideMs);
    return () => clearInterval(timerRef.current);
  }, [paused, slides.length, slideMs]);

  useEffect(() => {
    const onKey = (e) => {
      if (!slides.length) return;
      if (e.key === 'ArrowRight') setI(p => (p + 1) % slides.length);
      if (e.key === 'ArrowLeft') setI(p => (p - 1 + slides.length) % slides.length);
      if (e.key === ' ') setPaused(p => !p);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slides.length]);

  if (err) return <div className="w-screen h-screen bg-[#0b1220] text-red-400 flex items-center justify-center text-4xl">{err}</div>;
  if (!data) return <div className="w-screen h-screen bg-[#0b1220] text-white flex items-center justify-center text-4xl">Laster…</div>;

  // Night mode: show dimmed logo screen
  if (isNight) {
    return (
      <div className="w-screen h-screen bg-black text-white flex flex-col items-center justify-center gap-8 relative overflow-hidden" data-testid="signage-night-mode">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 to-black opacity-90" />
        <Moon className="w-24 h-24 text-slate-600 relative z-10" />
        {data.org_logo_url && <img src={data.org_logo_url} alt="" className="max-h-48 opacity-40 relative z-10" />}
        <div className="text-slate-500 font-bold relative z-10" style={{ fontSize: 'clamp(28px, 3vw, 56px)' }}>{data.org_name}</div>
        <div className="text-slate-700 relative z-10" style={{ fontSize: 'clamp(14px, 1.1vw, 22px)' }}>God natt • Presentasjonsvisning starter kl. 06:00</div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen overflow-hidden bg-[#0b1220] relative" onClick={() => setPaused(p => !p)}>
      <div className="absolute top-6 right-6 z-20 text-white/70 text-lg flex items-center gap-2 pointer-events-none font-bold">
        {paused ? <><Pause className="w-5 h-5" /> Pauset – piltast for å bla</> : <><Play className="w-5 h-5" /> Auto</>}
      </div>
      {paused && slides.length > 1 && (
        <>
          <button className="absolute left-6 top-1/2 -translate-y-1/2 z-30 bg-white/10 hover:bg-white/20 p-4 rounded-full" data-testid="signage-prev"
            onClick={(e) => { e.stopPropagation(); setI(p => (p - 1 + slides.length) % slides.length); }}>
            <ChevronLeft className="w-12 h-12 text-white" />
          </button>
          <button className="absolute right-6 top-1/2 -translate-y-1/2 z-30 bg-white/10 hover:bg-white/20 p-4 rounded-full" data-testid="signage-next"
            onClick={(e) => { e.stopPropagation(); setI(p => (p + 1) % slides.length); }}>
            <ChevronRight className="w-12 h-12 text-white" />
          </button>
        </>
      )}
      <FadeSlide k={i}>{slides[i]}</FadeSlide>
    </div>
  );
}

// ---------- Slide builder ----------
// Resolve a raw URL into (embed_url, is_video, error). Supports YouTube (watch/shorts/youtu.be)
// and Vimeo (vimeo.com/ID). Non-video URLs are returned as-is. Blank returns null.
const resolveEmbedUrl = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return { url: null };
  let u;
  try { u = new URL(s); } catch (_) { return { url: null, error: 'Ugyldig URL' }; }
  const host = u.hostname.replace(/^www\./, '');
  if (host === 'youtube.com' || host === 'm.youtube.com') {
    let id = u.searchParams.get('v');
    if (!id && u.pathname.startsWith('/shorts/')) id = u.pathname.split('/')[2];
    if (!id && u.pathname.startsWith('/embed/')) id = u.pathname.split('/')[2];
    if (!id) return { url: null, error: 'Fant ikke YouTube-video-ID i URL-en' };
    return { url: `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1`, isVideo: true };
  }
  if (host === 'youtu.be') {
    const id = u.pathname.replace(/^\//, '').split('/')[0];
    if (!id) return { url: null, error: 'Fant ikke YouTube-video-ID i URL-en' };
    return { url: `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1`, isVideo: true };
  }
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const parts = u.pathname.replace(/^\//, '').split('/').filter(Boolean);
    const id = parts.find(p => /^\d+$/.test(p));
    if (!id) return { url: null, error: 'Fant ikke Vimeo-video-ID i URL-en' };
    return { url: `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&loop=1&controls=0&background=1`, isVideo: true };
  }
  return { url: s };
};

function buildSlides(data, qrUrl, merkeStats) {
  const s = data.slides;
  const settings = data.settings || {};
  const orgName = data.org_name;
  const orgLogo = data.org_logo_url;
  const total = 99;
  const frameProps = { orgName, orgLogo };
  // QR-kode midlertidig skjult (klubber kan ha flere baner) – behold logikk for senere.
  // eslint-disable-next-line no-unused-vars
  const qrEnabled = false;
  const withQr = { ...frameProps, qrUrl: qrEnabled ? qrUrl : null };

  const maxTrend = Math.max(1, ...(s.monthly_trend || []).map(x => x.value));
  const maxLeaders = Math.max(1, ...(s.top_skyteledere_1_5 || []).map(x => x.count), ...(s.top_skyteledere_6_10 || []).map(x => x.count));
  const maxRange = Math.max(1, ...(s.range_usage || []).map(x => x.value));

  const list = [];

  if (settings.show_klubbstatus !== false) {
    list.push(
      <Frame key="1" title="Klubbstatus" subtitle="Aktivitet akkurat nå" index={list.length + 1} {...frameProps}
        footerAccent={<>
          <span>Aktivitetsstatistikk</span>
          <span className="text-slate-800">Siste 30 dager • Alle disipliner</span>
          <span>Infoskjerm-visning <span className="underline">Aktivlogg</span></span>
        </>}>
        <div className="grid grid-cols-4 gap-6 flex-1">
          <BigKPI label="Totalt antall treningsøkter" value={s.status.total_sessions} colorClass="bg-emerald-700" Icon={Target}
                  deltaPct={s.status.total_sessions_delta_pct} />
          <BigKPI label="Ukens treningsøkter" value={s.status.sessions_this_week ?? '—'} colorClass="bg-blue-700" Icon={CalendarDays} />
          <BigKPI label="Dagens treningsøkter" value={s.status.sessions_today ?? 0} colorClass="bg-purple-700" Icon={Sun} />
          <BigKPI label="Totalt antall medlemmer" value={s.status.total_members} colorClass="bg-orange-700" Icon={Users}
                  deltaText={s.status.new_members_30d ? `+${s.status.new_members_30d} nye siste 30 dager` : null} deltaPositive={(s.status.new_members_30d || 0) > 0} />
        </div>
      </Frame>
    );
  }
  if (settings.show_activity !== false) {
    list.push(
      <Frame key="2" title="Aktivitetsstatistikk" subtitle={`${data.settings?.stats_period_label || 'Siste 30 dager'} • Alle disipliner`} index={list.length + 1} {...withQr}>
        <div className="grid grid-cols-4 grid-rows-2 gap-5 flex-1">
          <BigKPI label="Totale økter" value={s.activity_30d.sessions} colorClass="bg-blue-600" Icon={Target} valueScale={7}
                  deltaPct={s.activity_30d.delta_sessions_pct} />
          <BigKPI label="Aktive medlemmer" value={s.activity_30d.active_members} colorClass="bg-emerald-600" Icon={Users} valueScale={7}
                  deltaPct={s.activity_30d.delta_active_pct} />
          <BigKPI label="Verifisering" value={`${s.activity_30d.verified_pct}%`} colorClass="bg-amber-600" Icon={ShieldCheck} valueScale={7}
                  deltaText={`${s.activity_30d.verified_count ?? 0} av ${s.activity_30d.sessions ?? 0}`} deltaPositive />
          <BigKPI label="Snitt per dag" value={s.activity_30d.avg_per_day ?? '—'} colorClass="bg-purple-600" Icon={TrendingUp} valueScale={7}
                  deltaPct={s.activity_30d.delta_avg_pct} />
          <BigKPI label={`Medl. med 3 dugnader i år`} value={s.dugnad_gold_count ?? 0} colorClass="bg-orange-500" Icon={Award} valueScale={7}
                  deltaText={`≥ 3 dugnader i ${data.year}`} deltaPositive />
          <BigKPI label="Avviste registreringer" value={s.activity_30d.rejected ?? 0} colorClass="bg-rose-600" Icon={XCircle} valueScale={7}
                  deltaText="Totalt" deltaPositive={false} />
          <BigKPI label="Unike aktive brukere" value={s.activity_30d.unique_active ?? s.activity_30d.active_members} colorClass="bg-indigo-600" Icon={UserCheck} valueScale={7}
                  deltaText="Med treningsøkter" deltaPositive />
          <BigKPI label="Fødselsnummer registrert" value={`${s.activity_30d.birth_pct ?? 0}%`} colorClass="bg-teal-600" Icon={Fingerprint} valueScale={7}
                  deltaText={`${s.status.verified} av ${s.status.total_members}`} deltaPositive />
        </div>
      </Frame>
    );
  }
  if (settings.show_top_members !== false) {
    list.push(
      <Frame key="3" title="Mest aktive medlemmer" subtitle="Hvem og hva som står for mest aktivitet" index={list.length + 1} {...withQr}>
        <div className="grid grid-cols-2 gap-8 flex-1">
          <div className="bg-slate-800/50 rounded-3xl p-8 border border-slate-700/60 flex flex-col">
            <SectionTitle icon={<Users className="w-7 h-7 text-amber-400" />}>Topp 5 mest aktive medlemmer</SectionTitle>
            <div className="flex-1 flex flex-col gap-3">
              {(s.top_members || []).map((m, idx) => <RankRow key={idx} rank={idx + 1} name={m.name} value={m.count} unit="økter" />)}
              {(!s.top_members || !s.top_members.length) && <div className="text-slate-500 text-2xl">Ingen data</div>}
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-3xl p-8 border border-slate-700/60 flex flex-col">
            <SectionTitle dotColor="bg-emerald-400" icon={<BookOpen className="w-7 h-7 text-emerald-400" />}>Topp 5 Skytedagbok-brukere</SectionTitle>
            <div className="flex-1 flex flex-col gap-3">
              {(s.top_diary || []).map((m, idx) => <RankRow key={idx} rank={idx + 1} name={m.name} value={m.count} unit="oppf." dotColor="bg-emerald-400" />)}
              {(!s.top_diary || !s.top_diary.length) && <div className="text-slate-500 text-2xl">Ingen data</div>}
            </div>
          </div>
        </div>
      </Frame>
    );
  }
  if (settings.show_trend !== false) {
    list.push(
      <Frame key="4" title="Aktivitetsutvikling" subtitle="Trender, banefordeling og disiplin" index={list.length + 1} {...withQr}>
        <div className="grid grid-cols-1 grid-rows-2 gap-6 flex-1 min-h-0 mb-4" data-testid="signage-trend-grid">
          <div className="bg-slate-800/50 rounded-3xl px-8 py-5 border border-slate-700/60 flex flex-col min-h-0">
            <SectionTitle icon={<ActivityIcon className="w-7 h-7 text-amber-400" />}>Månedlig aktivitetstrend</SectionTitle>
            <div className="flex-1 min-h-0 flex items-end gap-3 relative">
              <svg viewBox="0 0 1100 200" preserveAspectRatio="none" className="w-full h-full">
                {[0, 1, 2, 3, 4].map(idx => (<line key={idx} x1="0" y1={idx * 40 + 20} x2="1100" y2={idx * 40 + 20} stroke="#334155" strokeWidth="0.5" strokeDasharray="4 4" />))}
                <polyline fill="none" stroke="#fbbf24" strokeWidth="3"
                  points={(s.monthly_trend || []).map((m, idx) => {
                    const x = (idx / Math.max(1, s.monthly_trend.length - 1)) * 1060 + 20;
                    const y = 180 - (m.value / maxTrend) * 160;
                    return `${x},${y}`;
                  }).join(' ')} />
                {(s.monthly_trend || []).map((m, idx) => {
                  const x = (idx / Math.max(1, s.monthly_trend.length - 1)) * 1060 + 20;
                  const y = 180 - (m.value / maxTrend) * 160;
                  return (<g key={idx}>
                    <circle cx={x} cy={y} r="4" fill="#fbbf24" />
                    <text x={x} y={y - 9} fill="#fff" fontSize="14" fontWeight="bold" textAnchor="middle">{m.value}</text>
                    <text x={x} y={197} fill="#94a3b8" fontSize="12" textAnchor="middle">{m.label}</text>
                  </g>);
                })}
              </svg>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-3xl px-8 py-5 border border-slate-700/60 flex flex-col min-h-0 overflow-hidden">
            <SectionTitle icon={<PieIcon className="w-7 h-7 text-amber-400" />}>Aktivitet per bane</SectionTitle>
            <div className={`flex-1 min-h-0 grid gap-x-12 gap-y-3 content-center ${(s.range_usage || []).length > 3 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {(s.range_usage || []).map((r, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-1/3 font-bold text-white truncate" style={{ fontSize: 'clamp(16px, 1.3vw, 24px)' }}>{r.label}</div>
                  <div className="flex-1 h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-400 to-yellow-500" style={{ width: `${(r.value / maxRange) * 100}%` }} />
                  </div>
                  <div className="text-amber-300 font-black" style={{ fontSize: 'clamp(18px, 1.5vw, 28px)' }}>{r.value}</div>
                </div>
              ))}
              {(!s.range_usage || !s.range_usage.length) && <div className="text-slate-500 text-2xl">Ingen data</div>}
            </div>
          </div>
        </div>
      </Frame>
    );
  }
  if (settings.show_upcoming !== false && (s.upcoming_events || []).length > 0) {
    const view = settings.upcoming_view || 'list';
    const subtitleByView = {
      day: 'Hendelser i dag', week: 'Neste 7 dager',
      month: 'Neste 31 dager', list: 'De neste hendelsene i kalenderen',
    };
    list.push(
      <Frame key="upcoming" title="Kommende aktiviteter" subtitle={subtitleByView[view] || subtitleByView.list} index={list.length + 1} {...withQr}>
        <div className="flex-1 flex flex-col gap-4 justify-start overflow-hidden">
          {(s.upcoming_events || []).map((e, idx) => {
            const dt = new Date(e.start_datetime);
            const dateStr = dt.toLocaleDateString('no-NO', { weekday: 'long', day: 'numeric', month: 'long' });
            const timeStr = dt.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' });
            return (
              <div key={idx} className="flex items-center gap-6 bg-slate-800/60 rounded-3xl p-6 border border-slate-700/60">
                <div className="w-24 h-24 bg-amber-500 rounded-2xl flex flex-col items-center justify-center text-slate-900 font-black">
                  <div style={{ fontSize: '38px', lineHeight: 1 }}>{dt.getDate()}</div>
                  <div className="uppercase" style={{ fontSize: '14px' }}>{dt.toLocaleDateString('no-NO', { month: 'short' })}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-white truncate" style={{ fontSize: 'clamp(24px, 2vw, 40px)' }}>{e.title}</div>
                  <div className="flex flex-wrap gap-4 mt-2 text-slate-300" style={{ fontSize: 'clamp(14px, 1.1vw, 22px)' }}>
                    <span className="flex items-center gap-2 capitalize"><CalendarDays className="w-5 h-5" />{dateStr}</span>
                    <span className="flex items-center gap-2"><Clock className="w-5 h-5" />{timeStr}</span>
                    {e.location && <span className="flex items-center gap-2"><MapPin className="w-5 h-5" />{e.location}</span>}
                  </div>
                </div>
                {e.event_type && (
                  <span className="rounded-full px-4 py-2 bg-amber-500/20 text-amber-300 font-bold uppercase tracking-wider" style={{ fontSize: 'clamp(11px, 0.9vw, 16px)' }}>{e.event_type}</span>
                )}
              </div>
            );
          })}
        </div>
      </Frame>
    );
  }
  if (settings.show_milestones !== false && ((s.milestones || []).length > 0 || s.records?.busiest_day || s.records?.dugnad_total_year)) {
    list.push(
      <Frame key="milestones" title="Milepæler og klubbrekorder" subtitle="Positive prestasjoner denne perioden" index={list.length + 1} {...withQr}>
        <div className="grid grid-cols-2 gap-8 flex-1">
          <div className="bg-slate-800/50 rounded-3xl p-8 border border-slate-700/60 flex flex-col">
            <SectionTitle icon={<Sparkles className="w-7 h-7 text-amber-400" />}>Medlemsmilepæler</SectionTitle>
            <div className="flex-1 flex flex-col gap-3 justify-center">
              {(s.milestones || []).length > 0 ? (s.milestones || []).map((m, idx) => (
                <div key={idx} className="flex items-center gap-5 bg-slate-900/60 rounded-2xl p-4 border border-amber-500/30">
                  <div className="w-14 h-14 rounded-full bg-amber-400 flex items-center justify-center"><Star className="w-8 h-8 text-slate-900" strokeWidth={2.5} /></div>
                  <div className="flex-1">
                    <div className="font-black text-white" style={{ fontSize: 'clamp(20px, 1.6vw, 32px)' }}>{m.name}</div>
                    <div className="text-amber-300 font-bold" style={{ fontSize: 'clamp(14px, 1.1vw, 22px)' }}>har nå fullført <b>{m.milestone}</b> treninger!</div>
                  </div>
                </div>
              )) : <div className="text-slate-500 text-2xl text-center">Ingen nye milepæler denne perioden</div>}
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-3xl p-8 border border-slate-700/60 flex flex-col">
            <SectionTitle icon={<Trophy className="w-7 h-7 text-amber-400" />}>Klubbrekorder</SectionTitle>
            <div className="flex-1 min-h-0 flex flex-col gap-4 justify-center">
              {s.records?.busiest_day && (
                <RecordBox testId="signage-record-busiest" label="Travleste dag noensinne" value={s.records.busiest_day.count}
                  text={`økter på ${fmtNoDate(s.records.busiest_day.date)}`} accent="amber" />
              )}
              {s.records?.least_busy_weekday && (
                <RecordBox testId="signage-record-least-busy" label="Minst travle dag" value={s.records.least_busy_weekday.weekday}
                  text={`${String(s.records.least_busy_weekday.avg).replace('.', ',')} økter i snitt siste 12 mnd`} accent="sky" />
              )}
              <RecordBox testId="signage-record-dugnad" label={`Dugnader i ${data.year}`} value={s.records?.dugnad_total_year ?? s.dugnad_total ?? 0}
                text="registrert hittil i år" accent="emerald" />
            </div>
          </div>
        </div>
      </Frame>
    );
  }
  if (settings.show_leaders_1_5 !== false) {
    list.push(
      <Frame key="5" title="Topp 10 aktive skyteledere – 1 til 5" subtitle="Siste 12 måneder" index={list.length + 1} {...withQr}>
        <div className="flex-1 flex flex-col gap-3">
          {(s.top_skyteledere_1_5 || []).map((t, idx) => (
            <div key={idx} className="flex items-center gap-6 bg-slate-800/50 rounded-2xl px-6 py-4 border border-slate-700/60">
              <div className="w-16 h-16 rounded-full bg-amber-400 flex items-center justify-center shrink-0 relative">
                <Crosshair className="w-9 h-9 text-slate-900" strokeWidth={2.5} />
                <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-slate-900 text-amber-300 font-black text-lg flex items-center justify-center">{idx + 1}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate text-white" style={{ fontSize: 'clamp(22px, 1.9vw, 40px)' }}>{t.name}</div>
                <div className="h-3 bg-slate-700 rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-400 to-yellow-500" style={{ width: `${(t.count / maxLeaders) * 100}%` }} />
                </div>
              </div>
              <div className="text-amber-300 font-black whitespace-nowrap" style={{ fontSize: 'clamp(32px, 2.8vw, 66px)' }}>{t.count} <span className="text-slate-400 font-bold ml-2" style={{ fontSize: 'clamp(12px, 0.9vw, 18px)' }}>verifiseringer</span></div>
            </div>
          ))}
          {(!s.top_skyteledere_1_5 || !s.top_skyteledere_1_5.length) && <div className="text-slate-500 text-3xl">Ingen data</div>}
        </div>
      </Frame>
    );
  }
  if (settings.show_leaders_6_10 !== false && (s.top_skyteledere_6_10 || []).length > 0) {
    list.push(
      <Frame key="6" title="Topp 10 aktive skyteledere – 6 til 10" subtitle="Siste 12 måneder" index={list.length + 1} {...withQr}>
        <div className="flex-1 flex flex-col gap-3">
          {(s.top_skyteledere_6_10 || []).map((t, idx) => (
            <div key={idx} className="flex items-center gap-6 bg-slate-800/50 rounded-2xl px-6 py-4 border border-slate-700/60">
              <div className="w-16 h-16 rounded-full bg-amber-400 flex items-center justify-center shrink-0 relative">
                <Crosshair className="w-9 h-9 text-slate-900" strokeWidth={2.5} />
                <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-slate-900 text-amber-300 font-black text-lg flex items-center justify-center">{idx + 6}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate text-white" style={{ fontSize: 'clamp(22px, 1.9vw, 40px)' }}>{t.name}</div>
                <div className="h-3 bg-slate-700 rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-400 to-yellow-500" style={{ width: `${(t.count / maxLeaders) * 100}%` }} />
                </div>
              </div>
              <div className="text-amber-300 font-black whitespace-nowrap" style={{ fontSize: 'clamp(32px, 2.8vw, 66px)' }}>{t.count} <span className="text-slate-400 font-bold ml-2" style={{ fontSize: 'clamp(12px, 0.9vw, 18px)' }}>verifiseringer</span></div>
            </div>
          ))}
        </div>
      </Frame>
    );
  }
  if (settings.show_dugnad !== false) {    list.push(
      <Frame key="7" title={`Dugnadsdeltakelse i ${data.year}`} subtitle={`Alle med registrert deltakelse: ${s.dugnad_total}`} index={list.length + 1} {...withQr}>
        <div className="rounded-2xl bg-amber-500/15 border border-amber-500/40 px-6 py-4 mb-4 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-amber-400/20 flex items-center justify-center"><Trophy className="w-10 h-10 text-amber-400" strokeWidth={2.5} /></div>
          <div className="font-black text-amber-300" style={{ fontSize: 'clamp(22px, 1.9vw, 38px)' }}>
            {(s.dugnad_gold_count ?? 0)} medlemmer har oppnådd gullkrone-status
            <span className="text-slate-300 font-bold ml-3" style={{ fontSize: 'clamp(14px, 1.1vw, 22px)' }}>(≥ 3 dugnader)</span>
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-4">
          {(s.dugnad_top || []).map((d, idx) => <RankRow key={idx} rank={idx + 1} name={d.name} value={d.count} unit="dugnader" />)}
          {(!s.dugnad_top || !s.dugnad_top.length) && <div className="text-slate-500 text-3xl">Ingen data</div>}
        </div>
      </Frame>
    );
  }
  // Merkeskyting-slide (kun hvis data finnes)
  if (merkeStats && merkeStats.total_series > 0 && settings.show_merkeskyting !== false) {
    const LEVEL_META = {
      nybegynner: { color: '#f97316', label: 'Nybegynner' },
      viderekommende: { color: '#94a3b8', label: 'Viderekommende' },
      bronse: { color: '#b45309', label: 'Bronse' },
      solv: { color: '#cbd5e1', label: 'Sølv' },
      gull: { color: '#eab308', label: 'Gull' },
    };
    const levels = merkeStats.levels_meta || [];
    list.push(
      <Frame key="merkeskyting" title="Merkeskyting" subtitle="NSFs ferdighetsmerker – klubbens fremgang" index={list.length + 1} {...withQr}>
        <div className="grid grid-cols-4 gap-6 mb-5">
          <BigKPI label="Serier registrert" value={merkeStats.total_series} colorClass="bg-blue-700" Icon={Target} valueScale={7} />
          <BigKPI label="Aktive deltakere" value={merkeStats.total_participants} colorClass="bg-emerald-700" Icon={Users} valueScale={7} />
          <BigKPI label="Merker oppnådd totalt" value={merkeStats.total_awards} colorClass="bg-yellow-600" Icon={Award} valueScale={7} />
          <BigKPI label="Nye merker siste 30 dager" value={merkeStats.awards_last_30_days} colorClass="bg-purple-700" Icon={Sparkles} valueScale={7} />
        </div>
        <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
          <div className="bg-slate-800/50 rounded-3xl p-6 border border-slate-700/60 flex flex-col">
            <SectionTitle icon={<Trophy className="w-7 h-7 text-amber-400" />}>Merker per nivå</SectionTitle>
            <div className="grid grid-cols-5 gap-3 flex-1 items-center">
              {levels.map((lv) => {
                const meta = LEVEL_META[lv.key] || {};
                const count = merkeStats.level_counts?.[lv.key] || 0;
                return (
                  <div key={lv.key} className="flex flex-col items-center gap-2">
                    <div className="relative flex items-center justify-center"
                         style={{ width: 'clamp(60px, 5.5vw, 110px)', height: 'clamp(60px, 5.5vw, 110px)' }}>
                      <img src={`/badges/${lv.key}.png`} alt={meta.label} className="w-full h-full object-contain" />
                      <span className="absolute -bottom-1 right-0 bg-slate-900 text-white font-black rounded-full px-2 py-0.5 border-2 border-amber-400" style={{ fontSize: 'clamp(14px, 1.2vw, 22px)' }}>{count}</span>
                    </div>
                    <span className="text-slate-300 font-bold text-center" style={{ fontSize: 'clamp(11px, 0.9vw, 18px)' }}>{meta.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-3xl p-6 border border-slate-700/60 flex flex-col">
            <SectionTitle dotColor="bg-emerald-400" icon={<TrendingUp className="w-7 h-7 text-emerald-400" />}>Topp 5 aktive merkeskyttere</SectionTitle>
            <div className="flex-1 flex flex-col gap-2">
              {(merkeStats.top_shooters || []).slice(0, 5).map((sh, idx) => (
                <RankRow key={idx} rank={idx + 1} name={sh.name} value={sh.count} unit="serier" />
              ))}
              {(!merkeStats.top_shooters || !merkeStats.top_shooters.length) && (
                <div className="text-slate-500 text-2xl">Ingen data</div>
              )}
            </div>
          </div>
        </div>
        {merkeStats.recent_awards?.length > 0 && (
          <div className="mt-5 bg-gradient-to-r from-amber-600/30 to-yellow-500/20 border border-amber-400/40 rounded-2xl p-4 flex items-center gap-5">
            <div className="flex items-center gap-2 text-amber-300 font-black whitespace-nowrap" style={{ fontSize: 'clamp(14px, 1.3vw, 24px)' }}>
              <Sparkles className="w-6 h-6" /> Nyeste merker
            </div>
            <div className="flex-1 flex gap-4 overflow-hidden">
              {merkeStats.recent_awards.slice(0, 4).map((a, idx) => {
                const c = LEVEL_META[a.level] || {};
                return (
                  <div key={idx} className="flex items-center gap-2 bg-slate-900/40 rounded-lg px-3 py-2">
                    <img src={`/badges/${a.level}.png`} alt={c.label} className="object-contain" style={{ width: 'clamp(28px, 2.4vw, 48px)', height: 'clamp(28px, 2.4vw, 48px)' }} />
                    <div>
                      <p className="text-white font-bold" style={{ fontSize: 'clamp(12px, 1vw, 20px)' }}>{a.member_name}</p>
                      <p className="text-amber-200" style={{ fontSize: 'clamp(10px, 0.8vw, 16px)' }}>{c.label} • {a.achieved_at}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Frame>
    );
  }

  if (settings.show_iframe !== false) {
    const urls = (Array.isArray(settings.iframe_urls) && settings.iframe_urls.length)
      ? settings.iframe_urls
      : ((settings.iframe_url || '').trim() ? [settings.iframe_url] : []);
    urls.forEach((rawUrl, urlIdx) => {
      const cleanRaw = String(rawUrl || '').trim();
      if (!cleanRaw) return;
      const { url: embedUrl, isVideo, error: embedErr } = resolveEmbedUrl(cleanRaw);
      if (embedUrl) {
        list.push(
          <Frame key={`iframe-${urlIdx}`} title={isVideo ? 'Video' : 'Ekstern visning'} subtitle={cleanRaw} index={list.length + 1} {...withQr}>
            <div className="flex-1 flex" style={{ paddingLeft: '10%', paddingRight: '10%' }}>
              <iframe
                src={embedUrl}
                title={isVideo ? 'Video' : 'Ekstern visning'}
                className="w-full h-full rounded-2xl border border-slate-700/60 bg-black"
                {...(isVideo ? {} : { sandbox: 'allow-scripts allow-same-origin allow-popups allow-forms allow-presentation', referrerPolicy: 'no-referrer' })}
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media; accelerometer; gyroscope"
                allowFullScreen
                data-testid={`signage-iframe-${urlIdx}`}
              />
            </div>
          </Frame>
        );
      } else if (embedErr) {
        list.push(
          <Frame key={`iframe-err-${urlIdx}`} title="Ekstern visning – feil" subtitle={cleanRaw} index={list.length + 1} {...withQr}>
            <div className="flex-1 flex items-center justify-center" style={{ paddingLeft: '10%', paddingRight: '10%' }}>
              <div className="text-center bg-rose-900/40 border border-rose-500/50 rounded-3xl p-12">
                <XCircle className="w-20 h-20 text-rose-400 mx-auto mb-4" />
                <div className="text-white font-black mb-2" style={{ fontSize: 'clamp(24px, 2vw, 40px)' }}>Kan ikke vise denne lenken</div>
                <div className="text-rose-200" style={{ fontSize: 'clamp(16px, 1.2vw, 24px)' }}>{embedErr}</div>
                <div className="text-slate-400 mt-4" style={{ fontSize: 'clamp(13px, 1vw, 20px)' }}>Kun YouTube- og Vimeo-lenker eller vanlige nettadresser støttes.</div>
              </div>
            </div>
          </Frame>
        );
      }
    });
  }
  return list;
}
